import { ClearOutlined as ClearOutlinedIcon, Search as SearchIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import is from '@sindresorhus/is';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../shared/source/context/context';
import { TextButtonWithIcon } from '../../design/components/button/TextButtonWithIcon';
import { useNavigationToEntity } from '../common/use-navigation-to-entity';
import { useManySkillsQuery } from '../query-hooks/skills';

import type { DropdownData } from './DropDown';
import type { Skill, SkillShort } from '../../../../shared/source/data-contract/skill';
import type { Palette } from '@mui/material/styles';
import type { ChangeEventHandler, FunctionComponent, KeyboardEvent, MouseEventHandler, SetStateAction } from 'react';

type GlobalSearchInputProps = {
    readonly currentCursor: number;
    readonly dropDownData: readonly DropdownData[];
    readonly getActiveDropdownElement: () => DropdownData | undefined;
    readonly handleGlobalSearch: () => void;
    readonly onCloseDropdown: () => void;
    readonly onOpenDropdown: () => void;
    readonly searchTerm: string;
    readonly setCurrentCursor: (value: SetStateAction<number>) => void;
    readonly setSearchTerm: (value: SetStateAction<string>) => void;
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
};

type GlobalSearchSkillChipProps = {
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
    readonly skills: readonly SkillShort[];
};

type PaletteKey<T extends keyof Palette> = T;

type ChipColor = PaletteKey<'hardskill' | 'language' | 'softskill'>;

const skillTypeColorMapper: Record<Skill['skillType'], ChipColor> = {
    hard: 'hardskill',
    language: 'language',
    soft: 'softskill',
};

const getSkillColor = (type: SkillShort['skillType']): ChipColor => {
    return skillTypeColorMapper[type];
};

const GlobalSearchSkillChip: FunctionComponent<GlobalSearchSkillChipProps> = ({ setSkillIds, skillIds, skills }) => {
    const { actions } = useContext(ApplicationContext);
    const theme = useTheme();

    const handleDelete = (skillId: string): void => {
        const updatedSkillIds = skillIds.filter((id) => id !== skillId);

        setSkillIds(updatedSkillIds);
    };

    return (
        <Box
            alignItems="center"
            aria-label="Global Search Skill Chips"
            display="flex"
            flexWrap="wrap"
            gap={0.25}
            justifyContent="start"
            paddingBottom={1}
        >
            {skills.map(({ id, skillType, title }) => {
                const currentTypeColor = getSkillColor(skillType);
                const color = theme.palette[currentTypeColor].main;

                return (
                    <TextButtonWithIcon
                        key={id}
                        backgroundColor={color}
                        Icon={ClearOutlinedIcon}
                        label={title}
                        textColor="#FFF"
                        onIconClick={() => handleDelete(id)}
                        onLabelClick={() => actions.openSkillDetails(id)}
                    />
                );
            })}
        </Box>
    );
};

type ExactSkillsSectionProps = {
    readonly setSkillIds: (value: SetStateAction<readonly string[]>) => void;
    readonly skillIds: readonly string[];
};

const ExactSkillsSection: FunctionComponent<ExactSkillsSectionProps> = ({ setSkillIds, skillIds }) => {
    const { data } = useManySkillsQuery({ enabled: is.nonEmptyArray(skillIds), skillIds });

    if (is.undefined(data) || is.emptyArray(data)) {
        return null;
    }

    return <GlobalSearchSkillChip setSkillIds={setSkillIds} skillIds={skillIds} skills={data} />;
};

export const GlobalSearchInput: FunctionComponent<GlobalSearchInputProps> = ({
    dropDownData,
    getActiveDropdownElement,
    handleGlobalSearch,
    onCloseDropdown,
    onOpenDropdown,
    searchTerm,
    setCurrentCursor,
    setSearchTerm,
    setSkillIds,
    skillIds,
}) => {
    const theme = useTheme();
    const { navigateToJobPage, navigateToLearningOpportunityPage, navigateToUserProfile } = useNavigationToEntity();
    const { translate } = useContext(ApplicationContext);

    const onAddSkill = (skillId: string): void => {
        setSkillIds([...skillIds, skillId]);
    };

    const onDeleteLastSkill = (): void => {
        const updatedSkillIds = skillIds.slice(0, -1);
        setSkillIds(updatedSkillIds);
    };

    const changeSearchTerm: ChangeEventHandler<HTMLInputElement> = (event): void => {
        const searchValue = event.target.value;
        onOpenDropdown();
        setSearchTerm(searchValue);
    };

    const handleClick: MouseEventHandler = (event) => {
        event.stopPropagation();
        handleGlobalSearch();
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
        e.stopPropagation();

        switch (e.key) {
            case 'Enter': {
                const active = getActiveDropdownElement();

                if (is.undefined(active)) {
                    handleGlobalSearch();
                    break;
                }

                const { id, type } = active;
                if (type === 'job') {
                    navigateToJobPage(id);
                }
                if (type === 'skill') {
                    onAddSkill(id);
                }
                if (type === 'user') {
                    navigateToUserProfile(id);
                }
                if (type === 'learning-opportunity') {
                    navigateToLearningOpportunityPage(id);
                }

                onCloseDropdown();
                setSearchTerm('');
                setCurrentCursor(-1);
                break;
            }
            case 'ArrowUp':
                setCurrentCursor((prevState) => (prevState > 0 ? prevState - 1 : -1));
                break;
            case 'ArrowDown':
                setCurrentCursor((prevState) => (prevState === dropDownData.length - 1 ? 0 : prevState + 1));
                break;
            case 'Backspace':
                if (is.emptyString(searchTerm)) {
                    onDeleteLastSkill();
                }
                break;
            default:
                break;
        }
    };

    return (
        <Grid
            aria-label="Global Search Input"
            sx={{
                border: `1px solid ${theme.palette.cardborder.main}`,
                borderRadius: '8px',
                display: 'flex',
                justifyContent: 'space-between',
                padding: '9px 12px',
            }}
        >
            <Grid width="100%">
                <ExactSkillsSection setSkillIds={setSkillIds} skillIds={skillIds} />
                <TextField
                    aria-label="Global Search Input Field"
                    autoComplete="off"
                    fullWidth={true}
                    id="global-search-input"
                    InputProps={{ disableUnderline: true }}
                    placeholder={translate('pages.global-search.placeholder')}
                    size="small"
                    sx={{ '& .MuiInputBase-input': { padding: 0 }, border: 'none' }}
                    value={searchTerm}
                    variant="standard"
                    onChange={changeSearchTerm}
                    onClick={onOpenDropdown}
                    onKeyDown={handleKeyPress}
                />
            </Grid>
            <IconButton
                disableRipple={true}
                id="global-search-button"
                sx={{ height: '24px', width: '24px' }}
                onClick={handleClick}
            >
                <SearchIcon />
            </IconButton>
        </Grid>
    );
};
